import { barcodeSvg, escapeXml } from '../barcode/svg'
import { Block, PrintDocument, RenderOutput } from '../document'

/**
 * HTML for the browser print dialog: receipts at roll width, single labels at their size, A4 sheets of labels / cards.
 * Unicode prints here (Bangla included), unlike the byte protocols. QR codes are drawn by the caller's `qr` hook
 * (the `qrcode` library lives in the apps), else their text shows.
 */
export interface HtmlOptions {
  qr?: (value: string, sizeMm: number) => string
  title?: string
}

const alignCss = (a?: string) => (a ? `text-align:${a};` : '')

const blockHtml = (b: Block, o: HtmlOptions): string => {
  switch (b.type) {
    case 'text':
      return `<div style="${alignCss(b.align)}${b.bold ? 'font-weight:700;' : ''}${b.size === 2 ? 'font-size:1.8em;' : ''}">${escapeXml(b.text)}</div>`
    case 'row':
      return `<div class="row"${b.bold ? ' style="font-weight:700"' : ''}><span>${escapeXml(b.left)}</span><span>${escapeXml(b.right)}</span></div>`
    case 'rule':
      return '<div class="rule"></div>'
    case 'feed':
      return `<div style="height:${b.lines * 1.2}em"></div>`
    case 'barcode':
      return `<div style="${alignCss(b.align ?? 'center')}">${barcodeSvg(b.value, b.symbology, { heightMm: b.height, showText: b.show_text })}</div>`
    case 'qr':
      return `<div style="${alignCss(b.align ?? 'center')}">${o.qr ? o.qr(b.value, b.size ?? 20) : escapeXml(b.value)}</div>`
    case 'image':
      return `<div style="${alignCss(b.align ?? 'center')}"><img src="${escapeXml(b.src)}" style="width:${b.width ?? 30}mm"></div>`
  }
}

const BASE_CSS = `
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:ui-monospace,Menlo,Consolas,monospace;color:#000;background:#fff}
.row{display:flex;justify-content:space-between;gap:2mm}
.rule{border-top:1px dashed #000;margin:1.5mm 0}
svg{display:inline-block}
`

export const renderHtml = (doc: PrintDocument, p: Record<string, any>, o: HtmlOptions = {}): RenderOutput => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const fontPt = Number(p.font_size_pt ?? 9)
  let page: string
  let body: string
  if (doc.kind === 'sheet') {
    const { columns, rows, cell_width_mm: w, cell_height_mm: h, margin_mm = 10, gap_mm = 2 } = doc.layout
    page = `@page{size:A4;margin:${margin_mm}mm}`
    const perPage = columns * rows
    const pages: string[] = []
    for (let i = 0; i < doc.cells.length; i += perPage) {
      const cells = doc.cells
        .slice(i, i + perPage)
        .map((blocks) => `<div class="cell">${blocks.map((b) => blockHtml(b, o)).join('')}</div>`)
        .join('')
      pages.push(`<section class="sheet">${cells}</section>`)
    }
    body = pages.join('')
    page += `.sheet{display:grid;grid-template-columns:repeat(${columns},${w}mm);grid-auto-rows:${h}mm;gap:${gap_mm}mm;page-break-after:always}
.sheet:last-child{page-break-after:auto}.cell{overflow:hidden;padding:2mm;border:1px dotted #bbb}`
  } else if (doc.kind === 'label') {
    const w = Number(p.label_width_mm ?? 50)
    const h = Number(p.label_height_mm ?? 30)
    page = `@page{size:${w}mm ${h}mm;margin:0}.label{width:${w}mm;height:${h}mm;padding:2mm;overflow:hidden;page-break-after:always}`
    const one = `<div class="label">${doc.blocks.map((b) => blockHtml(b, o)).join('')}</div>`
    body = one.repeat(Math.max(1, doc.copies ?? 1))
  } else {
    const w = Number(p.width_mm ?? 80)
    page = p.page === 'a4' ? `@page{size:A4;margin:15mm}.roll{width:${w}mm;margin:0 auto}` : `@page{size:${w}mm auto;margin:0}.roll{width:${w}mm;padding:3mm}`
    body = `<div class="roll">${doc.blocks.map((b) => blockHtml(b, o)).join('')}</div>`
  }
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeXml(o.title ?? 'Print')}</title><style>${BASE_CSS}body{font-size:${fontPt}pt}${page}</style></head><body>${body}</body></html>`
  return { text: html, mime: 'text/html' }
}
